import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  useGetAllAvailabilityQuery,
  useGetDateSpecificAvailabilityQuery,
  useUpdateAllAvailabilityMutation,
  useUpdateDateSpecificAvailabilityMutation
} from '../../../../../store/services/tutor/AvailabilityService';
import { validateSlots } from '../../../../../utils/utils';
import { TimeRangePicker } from '../../../../components/Availability/TimeRangePicker';
import { WeeklyAvailability } from '../../../../components/Availability/WeeklyAvailability';
import { showErrorToast, showSuccessToast } from '../../../../components/PluginsMenu/Toastr/Toastr';
import { GenericModal } from '../../../../components/common/Modal/GenericModal';
import '../../../../styles/datepicker.css';
import { daysOfWeek } from "../../../../../constants";

//const daysOfWeek = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

export const TutorAvailability = () => {
  const [weeklyHours, setWeeklyHours] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  // API hooks
  const { data: weeklyAvailabilityData, refetch: refetchAllAvailability } = useGetAllAvailabilityQuery({
    refetchOnMountOrArgChange: true,
  });
  const [updateAllAvailability,  ] = useUpdateAllAvailabilityMutation();
  const [updateDateSpecificAvailability, ] = useUpdateDateSpecificAvailabilityMutation();
  const { data: dateAvailability, refetch: refetchDateAvailability } =
  useGetDateSpecificAvailabilityQuery(
    selectedDate ? { Date: selectedDate.toISOString() } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    refetchAllAvailability()
    refetchDateAvailability()
  }, [])

  // when API responds, set the slots
  useEffect(() => {
    if (dateAvailability?.data?.length > 0) {
      const slots = dateAvailability.data[0].slots.map((slot) => ({
        startTime: slot.startTime, // "10:00:00"
        endTime: slot.endTime      // "12:00:00"
      }));
      setTimeSlots(slots);
    } else {
      setTimeSlots([]); // no availability for this date
    }
  }, [dateAvailability]);

  // map API response to weeklyHours
  useEffect(() => {
    if (weeklyAvailabilityData) {
      const mapped = {};
      daysOfWeek.forEach(day => {
        const apiDay = weeklyAvailabilityData.data?.find(d => d.day === day);
        if (apiDay) {
          mapped[day] = {
            enabled: apiDay.slots.length > 0,
            slots: apiDay.slots.map(slot => ({
              startTime: slot.startTime,
              endTime: slot.endTime
            })),
            errors: {}
          };
        } else {
          mapped[day] = { enabled: false, slots: [], errors: {} };
        }
      });
      setWeeklyHours(mapped);
    }
  }, [weeklyAvailabilityData]);

  // ---------- WEEKLY HANDLERS ----------
  const addTimeSlot = (day) => {
    setWeeklyHours((prev) => {
      const newSlot = { startTime: '09:00:00', endTime: '17:00:00' };
      const updatedSlots = [...prev[day].slots, newSlot];
      const updatedErrors = validateSlots(updatedSlots);
      return {
        ...prev,
        [day]: { ...prev[day], slots: updatedSlots, errors: updatedErrors, enabled: true }
      };
    });
  };

  const removeTimeSlot = (day, index) => {
    setWeeklyHours((prev) => {
      const updatedSlots = prev[day].slots.filter((_, i) => i !== index);
      const updatedErrors = validateSlots(updatedSlots);
      return {
        ...prev,
        [day]: {
          ...prev[day],
          slots: updatedSlots,
          errors: updatedErrors,
          enabled: updatedSlots.length > 0
        }
      };
    });
  };

  const updateSlot = (day, index, field, value) => {
    setWeeklyHours((prev) => {
      const updatedSlots = prev[day].slots.map((slot, i) =>
        i === index ? { ...slot, [field]: value.value } : slot
      );
      const updatedErrors = validateSlots(updatedSlots);
      return {
        ...prev,
        [day]: { ...prev[day], slots: updatedSlots, errors: updatedErrors }
      };
    });
  };

  const saveSchedule = async () => {
    const hasErrors = Object.values(weeklyHours).some(day => Object.keys(day.errors).length > 0);
    if (hasErrors) {
      showErrorToast("Please resolve all scheduling conflicts before saving.");
      return;
    }

    const payload = {
      days: Object.entries(weeklyHours).map(([day, { slots }]) => ({
        dayOfWeek: day,
        slots: slots.map(slot => ({
          start: slot.startTime,
          end: slot.endTime
        }))
      }))
    };

    try {
      console.log("update payload = ", payload);
      const response = await updateAllAvailability(payload).unwrap();
      console.log("response update availability= ", response.succeeded)
      if(response.succeeded) {
        showSuccessToast("Weekly Availability saved!");
        await refetchAllAvailability();
      }
    } catch (err) {
      showErrorToast("Failed to save weekly availability");
    }
  };

  // handle date change (single date only)
  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const addSpecificTimeSlot = () => {
    const newSlot = { startTime: "09:00:00", endTime: "17:00:00" };
    const updatedSlots = [...timeSlots, newSlot];
    const updatedErrors = validateSlots(updatedSlots);
    setTimeSlots(updatedSlots);
    setErrors(updatedErrors);
  };

  const removeSpecificTimeSlot = (index) => {
    const updatedSlots = timeSlots.filter((_, i) => i !== index);
    const updatedErrors = validateSlots(updatedSlots);
    setTimeSlots(updatedSlots);
    setErrors(updatedErrors);
  };

  const updateTimeSlot = (index, field, value) => {
    const updatedSlots = timeSlots.map((slot, i) =>
      i === index ? { ...slot, [field]: value.value } : slot
    );
    const updatedErrors = validateSlots(updatedSlots);
    setTimeSlots(updatedSlots);
    setErrors(updatedErrors);
  };

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setTimeSlots([]);
    setSelectedDate(new Date());
  };

  const applyTimeSlotsToDate = async () => {
    if (!selectedDate) {
      showErrorToast("Please select a date first.");
      return;
    }
    const hasErrors = Object.values(errors).some((error) => error !== null);
    if (hasErrors) {
      showErrorToast("Please resolve conflicts before saving.");
      return;
    }

    const payload = {
      date: selectedDate.toISOString(),
      timeSlots: timeSlots.map((slot) => ({
        start: slot.startTime,
        end: slot.endTime,
      })),
    };

    try {
      const res = await updateDateSpecificAvailability(payload).unwrap();
      if (res?.succeeded) {
        showSuccessToast("Date-specific availability saved!");
        await refetchDateAvailability();
        handleCloseModal();
      } else {
        showErrorToast("Failed to save availability");
      }
    } catch (err) {
      showErrorToast("Failed to save date-specific availability");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="card shadow">
        <div className="row g-0">
          <WeeklyAvailability
            weeklyHours={weeklyHours}
            setWeeklyHours={setWeeklyHours}
            saveSchedule={saveSchedule}
            addTimeSlot={addTimeSlot}
            removeTimeSlot={removeTimeSlot}
            updateSlot={updateSlot}
          />
          <div className="col-12 col-lg-6">
            <div className="card-body">
              <h2 className="card-title h4">Date Specific</h2>
              <p className="text-muted mb-0">Override your availability for specific dates when your hours differ from your regular week hours</p>
              <button onClick={handleOpenModal} className="btn btn-primary mt-3">
                Open Date-Specific Availability
              </button>
            </div>
          </div>
        </div>
      </div>

      <GenericModal
        show={showModal}
        handleClose={handleCloseModal}
        title="Date-Specific Availability"
        handleSave={applyTimeSlotsToDate}
      >
        <div>
          <div className="d-flex justify-content-center">
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              inline
              highlightDates={[selectedDate]}
              shouldCloseOnSelect={false}
              minDate={new Date()}
            />
          </div>

          {selectedDate && (
            <div className="mt-3">
              <label>Time Slots:</label>
              {timeSlots?.length && timeSlots.map((slot, index) => (
                <TimeRangePicker
                  key={index}
                  startTime={slot.startTime}
                  endTime={slot.endTime}
                  onStartTimeChange={(time) => updateTimeSlot(index, "startTime", time)}
                  onEndTimeChange={(time) => updateTimeSlot(index, "endTime", time)}
                  onRemove={() => removeSpecificTimeSlot(index)}
                  error={errors[index]}
                />
              ))}
              <button onClick={addSpecificTimeSlot} className="btn btn-link text-primary">
                <i className="bi bi-plus-circle me-1"></i>
                Add Time Slot
              </button>
            </div>
          )}
        </div>
      </GenericModal>
    </div>
  );
};